/**
 * Parse an API date string (YYYY-MM-DD or ISO datetime) as a calendar date in the user's local timezone.
 * Avoids the UTC-midnight shift from `new Date('YYYY-MM-DD')`, which can show the previous day in US timezones.
 */
export function dateStringToLocalDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

/**
 * Returns the next business day from the given date (or today if omitted).
 * Skips weekends (Sat/Sun). Optionally skips dates in holidayDates (YYYY-MM-DD).
 * Returns date in YYYY-MM-DD format.
 */
export function getNextBusinessDayDateString(
  fromDate?: Date,
  holidayDates?: Set<string>
): string {
  const date = fromDate ? new Date(fromDate) : new Date();
  const isWeekend = (d: Date) => {
    const day = d.getDay();
    return day === 0 || day === 6;
  };
  const toYYYYMMDD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const isHoliday = (d: Date) => holidayDates?.has(toYYYYMMDD(d)) ?? false;
  const isBusinessDay = (d: Date) => !isWeekend(d) && !isHoliday(d);

  let current = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  while (!isBusinessDay(current)) {
    current.setDate(current.getDate() + 1);
  }
  return toYYYYMMDD(current);
}

/**
 * Returns the first business day on or after (today + 7 days).
 * Use for default project start date in "new project" UI (UAT: 1 week from today).
 * Skips weekends; optionally skips holidayDates (YYYY-MM-DD).
 */
export function getDefaultProjectStartDateString(
  holidayDates?: Set<string>
): string {
  const oneWeekFromToday = new Date();
  oneWeekFromToday.setDate(oneWeekFromToday.getDate() + 7);
  return getNextBusinessDayDateString(oneWeekFromToday, holidayDates);
}
